import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cicassService, auth } from "../../../_services";
import { ToastComponent } from "@syncfusion/ej2-react-notifications";
import { DateTime } from "luxon";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Grid,
  Container
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Componenti esterni
import AttendanceDialog from "./AttendanceDialog";
import FormazioneRiepilogo from './FormazioneRiepilogo';
import HelperHome from './helperHome';
import NextEventsWidget from './NextEventsWidget'; 
import NewsWidget from './NewsWidget';
import AttendanceSection from './AttendanceSection';

const apiEndpointAttendance = "attendance/detection";
const apiUrlNews = "board/dash";
const apiUrlSetting = "setting";

const Home = ({ history }) => {
  const { t } = useTranslation();
  
  // Refs
  const toastObj = useRef(null);
  const isMounted = useRef(true);

  // State
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [loadingFormazione, setLoadingFormazione] = useState(true);
  const [loadingNextEvents, setLoadingNextEvents] = useState(true);
  const [boardData, setBoardData] = useState([]);
  const [disableAttendance, setDisableAttendance] = useState("1");
  const [userData, setUserData] = useState(undefined);
  const [geolocation, setGeolocation] = useState({ city: null });
  const [isTimbratureDialogOpen, setIsTimbratureDialogOpen] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [disableTimbraButton, setDisableTimbraButton] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [nextShifts, setNextShifts] = useState([]);
  const [formazioneData, setFormazioneData] = useState({ 
          fatti: [], daFare: [], istruttore: [], oreTot: 0, oreIstr: 0 
        });

  const [toastInfo, setToastInfoState] = useState({
    title: "",
    content: "Successfully",
    cssClass: "e-toast-success",
    icon: "e-success toast-icons",
  });

  const toastPosition = { X: "Right", Y: "Bottom" };

  // Helper per mostrare i toast
  const showToast = (title, message) => {
    let newToast = { ...toastInfo };
    switch (title) {
      case "Warning":
        newToast = { title: "Warning", content: message, cssClass: "e-toast-warning", icon: "e-warning toast-icons" };
        break;
      case "Success":
        newToast = { title: "Success", content: message, cssClass: "e-toast-success", icon: "e-success toast-icons" };
        break;
      case "Error":
        newToast = { title: "Error", content: message, cssClass: "e-toast-danger", icon: "e-error toast-icons" };
        break;
      case "Information":
        newToast = { title: "Information", content: message, cssClass: "e-toast-info", icon: "e-info toast-icons" };
        break;
      default: break;
    }
    setToastInfoState(newToast);
    // Timeout breve per assicurarsi che lo stato del toast sia aggiornato prima di mostrarlo
    setTimeout(() => {
      if (toastObj.current) toastObj.current.show();
    }, 100);
  };

  const getGeolocation = () => {
    if (!navigator.geolocation) {
      setGeolocation({ city: "GPS non supportato" });
      setLoadingAttendance(false);
      return;
    }

    const geoOptions = {
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: 1000 * 60 * 1 
    };

    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      const weblang = localStorage.getItem("weblang") || "it";

      try {
        const response = await auth.checkGeoLocation(latitude, longitude, weblang);
        let geoData = { ...response.data };

        if (!geoData.city || geoData.city === "Località non trovata") {
          const cachedCity = sessionStorage.getItem("last_city_name");
          if (cachedCity) {
            geoData.city = cachedCity;
          } else {
            const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
            const osmRes = await fetch(nominatimUrl);
            const osmData = await osmRes.json();
            const foundCity = osmData.address?.city || osmData.address?.town || osmData.address?.village;
            
            if (foundCity) {
              geoData.city = foundCity;
              sessionStorage.setItem("last_city_name", foundCity);
            }
          }
        }

        if (isMounted.current) 
          { setGeolocation(geoData);
            setLoadingAttendance(false);
          }

      } catch (error) {
        console.error("Errore geolocalizzazione:", error);
        if (isMounted.current) {
          setGeolocation({ enabled: true, city: "Posizione acquisita" });
          setLoadingAttendance(false);
        }
      }
    }, (error) => {
      console.error("Errore hardware GPS:", error);
      if (isMounted.current) {
        setLoadingAttendance(false); // <-- DISATTIVA SE L'UTENTE RIFIUTA O VA IN TIMEOUT IL GPS
      }
    }, geoOptions);
  };

  const saveAttendance = async (message, attendanceCause, attendanceNote) => {
    try {
      const format = "yyyy-MM-dd'T'HH:mm:ss";
      const currentDate = DateTime.now().toFormat(format);
      let dataToUpdate = {
        id: -1,
        anaUserId: -1,
        fromDt: currentDate,
        toDt: null,
        anaAttendanceTypeId: attendanceCause,
        attendanceNote,
      };
      ;
     // 1. EFFETTUA IL SALVATAGGIO REALE SUL DB (Istruzione ripristinata)
      await cicassService.saveOp(apiEndpointAttendance, dataToUpdate);
      showToast("Success", message);
      
      // 2. CHIUDI SUBITO IL DIALOG E REIMPOSTA LO STATO DEL PULSANTE
      handleDialogClose();
      setDisableTimbraButton(false);

      // 3. ATTIVA IMMEDIATAMENTE IL LOADING DEL WIDGET (Mostra lo Skeleton)
      setLoadingAttendance(true);

      // 4. AVVIA IL FLUSSO GPS (che interrogherà il browser e spegnerà il loading alla fine)
      getGeolocation();

    } catch (error) {
      setIsSubmitDisabled(false);
      showToast("Error", "Impossibile salvare la timbratura. Riprova.");
    }
  };

  const handleDialogClose = () => {
    setIsTimbratureDialogOpen(false);
    setIsSubmitDisabled(false);
  };

  const mapFormazioneData = (res) => {
    const d = res.data || res;
    return {
      fatti: d.formazioniFatte || [],
      daFare: d.formazioniDaFare || [],
      istruttore: d.formazioniComeIstruttore || [],
      oreTot: d.totaleOre || 0,
      oreIstr: d.totaleOreIstruttore || 0
    };
  };
  const handleYearChange = async (newYear) => {
  setLoadingFormazione(true);
  try {
    const res = await cicassService.getAll(`formazione/utente/riepilogo/${newYear}`);
    setFormazioneData(mapFormazioneData(res));
  } catch (error) {
    console.error("Errore cambio anno:", error);
  } finally {
    setLoadingFormazione(false);
  }
};
  
  // Ciclo di vita: Montaggio
 // Ciclo di vita: Montaggio
useEffect(() => {
  isMounted.current = true;

  const initData = async () => {
    setLoadingFormazione(true);
    setLoadingAttendance(true);
    setLoadingNews(true);
    setLoadingNextEvents(true);
    getGeolocation();

    const results = await Promise.allSettled([
      cicassService.getAll(apiUrlNews),
      cicassService.getAll(`${apiUrlSetting}/DisableAttendance`),
      auth.getAnaData(),
      cicassService.getAll('shift/next-shifts/3'),
      cicassService.getAll(`formazione/utente/riepilogo/${new Date().getFullYear()}`),
    ]);

    if (!isMounted.current) return;

    const [newsRes, settingRes, anaRes, shiftsRes, formazioneRes] = results;

    if (newsRes.status === 'fulfilled') {
      setBoardData(newsRes.value.data);
    } else {
      console.error("Errore news:", newsRes.reason);
    }

    if (settingRes.status === 'fulfilled') {
      setDisableAttendance(settingRes.value.data.value === "1" ? "1" : "0");
    } else {
      console.error("Errore setting:", settingRes.reason);
    }

    if (anaRes.status === 'fulfilled') {
      setUserData({ ...anaRes.value.data, userRole: auth.getUserRole() });
    } else {
      console.error("Errore anaData:", anaRes.reason);
    }

    if (shiftsRes.status === 'fulfilled') {
      setNextShifts(shiftsRes.value.data || []);
    } else {
      console.error("Errore shifts:", shiftsRes.reason);
    }

    if (formazioneRes.status === 'fulfilled') {
      setFormazioneData(mapFormazioneData(formazioneRes.value));
    } else {
      console.error("Errore formazione:", formazioneRes.reason);
      // formazioneData resta al valore iniziale (vuoto)
    }

    setLoadingFormazione(false);
    setLoadingAttendance(false);
    setLoadingNews(false);
    setLoadingNextEvents(false);
  };

  initData();

  return () => {
    isMounted.current = false;
  };
}, []);
 
   return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
        <Typography variant="h5" id="home-welcome" sx={{ fontWeight: 900, color: '#1a365d' }}>
          {t("DASHBOARD")}
        </Typography>

        <Tooltip title={t("Guida")}>
          <IconButton 
            onClick={() => setRunTour(true)} 
            sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', color: '#1a365d' }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* AREA SIDEBAR (OPERATIVA) - Prima su Mobile, Seconda su PC */}
        <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
          {disableAttendance === "0" && (
            <AttendanceSection 
              t={t}
              geolocation={geolocation}
              disableTimbraButton={disableTimbraButton}
              onGeolocationRefresh={getGeolocation}
              onAttendanceClick={() => setIsTimbratureDialogOpen(true)}
              disableAttendance={disableAttendance}
              loading={loadingAttendance}
            />
          )}
          <NextEventsWidget t={t} events={nextShifts} loading={loadingNextEvents}/>
        </Grid>

        {/* AREA MAIN (INFORMATIVA) - Seconda su Mobile, Prima su PC */}
        <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
          <NewsWidget boardData={boardData} t={t} loading={loadingNews}/>
          {formazioneData && (
            <Box sx={{ mt: 3 }}>
              <FormazioneRiepilogo t={t} history={history} data={formazioneData} loading={loadingFormazione} onYearChange={handleYearChange} />
            </Box>
          )}
        </Grid>
      </Grid>

      <HelperHome
        run={runTour} 
        onFinish={(data) => {
          if (['finished', 'skipped'].includes(data.status)) {
            setRunTour(false);
          }
        }}
        hasNews={boardData.length > 0}
        hasNextShifts={true}
        canShowAttendance={disableAttendance === '0'}
        hasRiepilogo={!!userData}
      />

      <ToastComponent
        ref={toastObj}
        id="toast_default"
        position={toastPosition}
        title={toastInfo.title}
        content={toastInfo.content}
        cssClass={toastInfo.cssClass}
        icon={toastInfo.icon}
      />

      <AttendanceDialog
        isOpen={isTimbratureDialogOpen}
        handleClose={handleDialogClose}
        isInSede={geolocation?.isInSede || false}
        onSubmit={(cause, note) => {
          setIsSubmitDisabled(true);
          saveAttendance(t("toastSuccess"), cause, note);
        }}
        translate={t}
        isSubmitDisabled={isSubmitDisabled}
      />
    </Container>
  );
};

export default Home;